import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { KeyPoint, Section } from './LegalPage';
import { COMMERCE, LEGAL_ENTITY, MEMBERSHIP, money, percent } from '../../config/legal';

const SECTIONS = [
  { id: 'agreement', title: 'This agreement' },
  { id: 'accounts', title: 'Accounts' },
  { id: 'shop', title: 'Shop purchases' },
  { id: 'memberships', title: 'Memberships' },
  { id: 'donations', title: 'Donations' },
  { id: 'advertising', title: 'Directory advertising' },
  { id: 'conduct', title: 'Acceptable use' },
  { id: 'your-content', title: 'Content you submit' },
  { id: 'our-content', title: 'Our content' },
  { id: 'availability', title: 'Availability' },
  { id: 'disclaimers', title: 'Disclaimers' },
  { id: 'liability', title: 'Limitation of liability' },
  { id: 'indemnity', title: 'Indemnity' },
  { id: 'termination', title: 'Suspension and termination' },
  { id: 'disputes', title: 'Governing law and disputes' },
  { id: 'general', title: 'General' },
];

export default function TermsOfService() {
  return (
    <LegalPage
      title="Terms of Service"
      summary="The agreement between you and Guhso covering the shop, memberships, donations, and directory advertising. Plain language where the law allows it."
      sections={SECTIONS}
    >
      <Section id="agreement" title="This agreement" index="1">
        <p>
          These terms are between you and <strong>{LEGAL_ENTITY.name}</strong> and govern your use
          of {LEGAL_ENTITY.site}, including the shop, memberships, donations, and directory
          advertising. By using the site or buying anything, you accept them.
        </p>
        <p>
          Our <Link to="/privacy">Privacy Policy</Link> and{' '}
          <Link to="/returns">Returns &amp; Shipping Policy</Link> form part of this agreement.
        </p>
        <p>
          Nothing here removes rights you have under mandatory consumer law where you live. Where
          a term conflicts with such a right, that right wins and the rest of these terms
          continue to apply.
        </p>
      </Section>

      <Section id="accounts" title="Accounts" index="2">
        <p>
          You need an account for memberships and some member content. We sign you in with a
          one-time code sent to your email address, so keep access to that mailbox secure — anyone
          who can read it can sign in as you.
        </p>
        <p>
          You must be at least 18, or old enough to enter a binding contract where you live, to
          buy anything. Give us accurate details and keep them current; we are not responsible for
          an order that fails because the address or email you supplied was wrong.
        </p>
      </Section>

      <Section id="shop" title="Shop purchases" index="3">
        <h3>Prices, tax, and shipping</h3>
        <p>
          Prices are in {COMMERCE.currency}. We add sales tax of{' '}
          <strong>{percent(COMMERCE.taxRate)}</strong> to taxable items. Shipping is{' '}
          <strong>calculated at checkout from live USPS rates</strong> for your address and
          parcel, and you choose the service. Orders with a shippable subtotal of{' '}
          <strong>{money(COMMERCE.freeShippingThreshold)} or more</strong> ship free up to{' '}
          <strong>{money(COMMERCE.freeShippingAbsorbCap)}</strong> of postage. Your total is
          always shown before you pay.
        </p>

        <h3>When the contract forms</h3>
        <p>
          Adding to a basket is not an order, and displaying a product is an invitation to buy
          rather than an offer. Your order is an offer to buy; the contract forms when we confirm
          the order. If an item turns out to be unavailable or a price was clearly wrong, we may
          decline the order and refund you in full.
        </p>

        <h3>Payment</h3>
        <p>
          We take payment through Square or Stripe. We never see your full card number — see the{' '}
          <Link to="/privacy">Privacy Policy</Link>. If a payment is reversed or charged back, we
          may suspend fulfilment and recover the amount owed.
        </p>

        <h3>Delivery and risk</h3>
        <p>
          Delivery estimates are estimates, not guarantees. Risk of loss passes to you on
          delivery. Import duties and taxes on international orders are yours to pay.
        </p>

        <KeyPoint>
          <p>
            Returns, exchanges, damaged goods, and refund timing are covered in the{' '}
            <Link to="/returns">Returns &amp; Shipping Policy</Link>.
          </p>
        </KeyPoint>
      </Section>

      <Section id="memberships" title="Memberships" index="4">
        <p>
          Membership unlocks subscriber-only content. Current pricing is{' '}
          <strong>{money(MEMBERSHIP.monthly)} per month</strong> or{' '}
          <strong>{money(MEMBERSHIP.annual)} per year</strong> in {MEMBERSHIP.currency}.
        </p>
        <KeyPoint tone="warn">
          <p>
            <strong>Memberships renew automatically</strong> at the end of each period, at the
            then-current price, until you cancel.
          </p>
        </KeyPoint>
        <ul>
          <li>Cancel at any time from your profile or by emailing us. Cancellation stops the next renewal.</li>
          <li>You keep access until the end of the period you have already paid for.</li>
          <li>We do not pro-rate part-used periods, except where consumer law requires it.</li>
          <li>We will tell you in advance before a price change takes effect, and you can cancel before it does.</li>
          <li>If a renewal payment fails, access may pause until payment succeeds.</li>
        </ul>
        <p>
          Member content is licensed to you personally for your own use. Do not redistribute it,
          share your access, or post it elsewhere.
        </p>
      </Section>

      <Section id="donations" title="Donations" index="5">
        <p>
          Donations support the show. You can give once or monthly, and monthly giving continues
          until you cancel.
        </p>
        <KeyPoint tone="warn">
          <p>
            <strong>Donations are gifts and are not refundable</strong>, because nothing is sold in
            exchange for them. If you gave by mistake or a recurring gift continued past a
            cancellation you intended, contact us — we deal with genuine errors in good faith.
          </p>
        </KeyPoint>
        <p>
          Unless we say so explicitly, a donation is not a charitable contribution for tax purposes
          and we do not issue tax receipts for one.
        </p>
      </Section>

      <Section id="advertising" title="Directory advertising" index="6">
        <p>
          The directory sells a fixed number of placements. Buying one books a campaign for the
          package's stated duration, on the dates you choose, subject to availability at the moment
          of purchase.
        </p>

        <h3>Booking and payment</h3>
        <ul>
          <li>The price is the package price shown at checkout. We price every campaign server-side, so the amount charged is always the published price.</li>
          <li>Placements have limited inventory. If a placement sells out while you are checking out, we decline the booking and take no payment.</li>
          <li>If you choose automatic renewal, the campaign renews at the end of each period until you cancel.</li>
        </ul>

        <h3>Approval and standards</h3>
        <p>
          Every advertisement is reviewed before it appears. We may refuse, edit for presentation,
          or withdraw any advertisement — including one already running — at our discretion, in
          particular where it is unlawful, misleading, offensive, infringing, or unsuitable for
          our audience. If we withdraw a compliant campaign for our own reasons, we refund the
          unused portion.
        </p>
        <p>You are responsible for your advertisement, and you confirm that:</p>
        <ul>
          <li>you have the right to use everything in it, including logos, artwork, and names;</li>
          <li>the claims in it are accurate and comply with advertising and consumer law; and</li>
          <li>the business or offer it promotes is genuine and lawful.</li>
        </ul>

        <h3>Performance</h3>
        <KeyPoint>
          <p>
            We sell <strong>placement</strong>, not results. We do not guarantee any number of
            impressions, clicks, enquiries, or sales. Reporting figures are our own measurement,
            provided in good faith for guidance.
          </p>
        </KeyPoint>
        <p>
          Paid placements are labelled as such throughout the directory. Campaigns are not
          refundable once live except as described above, and we may rotate placements among
          advertisers who bought the same position.
        </p>
      </Section>

      <Section id="conduct" title="Acceptable use" index="7">
        <p>Do not:</p>
        <ul>
          <li>break the law, or use the site to harm, harass, defraud, or impersonate anyone;</li>
          <li>upload malware, or attempt to breach, probe, or overload our systems;</li>
          <li>scrape, spider, or bulk-download the site, or resell access to it;</li>
          <li>copy our content beyond what copyright law permits; or</li>
          <li>interfere with anyone else's use of the site.</li>
        </ul>
      </Section>

      <Section id="your-content" title="Content you submit" index="8">
        <p>
          You keep ownership of everything you submit — comments, listings, artwork, advertising
          copy. You grant us a non-exclusive, worldwide, royalty-free licence to host, store,
          reproduce, adapt for formatting, and display it for the purpose of running the site and
          the campaign you bought.
        </p>
        <p>
          You confirm you have the rights to grant that licence. We may remove content that
          breaches these terms, and we are not obliged to store or return it.
        </p>
      </Section>

      <Section id="our-content" title="Our content" index="9">
        <p>
          Episodes, articles, artwork, branding, and the site itself belong to {LEGAL_ENTITY.name}
          or our licensors. You may enjoy and share links to public content, and quote briefly with
          credit. You may not republish, resell, or use our material commercially without written
          permission. Merchandise designs are licensed for personal use only.
        </p>
      </Section>

      <Section id="availability" title="Availability" index="10">
        <p>
          We aim to keep the site available but do not promise uninterrupted service. We may
          change, suspend, or discontinue features, and may schedule maintenance. Where a change
          materially reduces something you have paid for, we will refund the unused portion.
        </p>
      </Section>

      <Section id="disclaimers" title="Disclaimers" index="11">
        <p>
          Except as these terms expressly state, and to the fullest extent the law allows, the site
          and everything on it are provided <strong>“as is”</strong> without warranties of any kind,
          whether express or implied, including merchantability, fitness for a particular purpose,
          and non-infringement.
        </p>
        <p>
          Our editorial content is for general information and entertainment. It is not
          professional, financial, legal, or medical advice. Listings in the directory are supplied
          by the businesses themselves; we do not endorse or vet them beyond our advertising
          standards, and any dealing you have with a listed business is between you and them.
        </p>
      </Section>

      <Section id="liability" title="Limitation of liability" index="12">
        <p>
          Nothing here limits liability for death or personal injury caused by our negligence, for
          fraud, or for anything else that cannot lawfully be limited.
        </p>
        <p>
          Subject to that, and to the fullest extent the law allows, we are not liable for indirect
          or consequential loss, loss of profit, revenue, goodwill, data, or anticipated savings.
        </p>
        <p>
          Our total liability arising out of or in connection with these terms is limited to the
          greater of the amount you paid us in the twelve months before the claim arose, or{' '}
          {money(100)}.
        </p>
      </Section>

      <Section id="indemnity" title="Indemnity" index="13">
        <p>
          If someone brings a claim against us because of your content, your advertisement, or your
          breach of these terms, you agree to cover our reasonable losses, costs, and legal fees
          arising from it.
        </p>
      </Section>

      <Section id="termination" title="Suspension and termination" index="14">
        <p>
          You may stop using the site, cancel a membership, or close your account at any time. We
          may suspend or terminate access if you breach these terms, if we are required to, or if
          continuing would expose us to legal risk.
        </p>
        <p>
          If we terminate your access without cause, we refund the unused portion of anything you
          have prepaid. Terms that by nature should survive termination — content licences,
          disclaimers, liability limits, indemnity, and governing law — do survive it.
        </p>
      </Section>

      <Section id="disputes" title="Governing law and disputes" index="15">
        <p>
          These terms are governed by the laws of{' '}
          <span className="legal-todo">{LEGAL_ENTITY.jurisdiction}</span>, and the courts of{' '}
          <span className="legal-todo">{LEGAL_ENTITY.jurisdiction}</span> have jurisdiction —
          without depriving you of the protection of mandatory consumer law where you live.
        </p>
        <p>
          Please contact us first. Most problems are resolved faster by email than by anything
          formal.
        </p>
      </Section>

      <Section id="general" title="General" index="16">
        <p>
          These terms, together with the Privacy Policy and Returns Policy, are the whole agreement
          between us. If a provision is unenforceable, the rest stands. Not enforcing a term is not
          a waiver of it. You may not transfer your rights under these terms without our consent;
          we may transfer ours if the business is sold, without reducing your rights.
        </p>
        <p>
          We may update these terms. Material changes will be posted here with a new effective
          date, and continuing to use the site afterwards means you accept them. Changes never
          apply retroactively to an order already placed.
        </p>
        <p>
          Questions: <a href={`mailto:${LEGAL_ENTITY.contactEmail}`}>{LEGAL_ENTITY.contactEmail}</a>
        </p>
      </Section>
    </LegalPage>
  );
}
